import React, { Fragment } from 'react';
import { Form } from '../../../ui/universe';
import { ChildMapRenderer } from '../components';
import { getType, isObjectEmptyValues } from '../../../../utils/index.js';
import invoiceConfig from '../../../../const/invoice.json';
import { getSelectRectKey } from '../utils';

const SIMPLE_TYPES = ['string', 'number', 'boolean'];

/**
 * 渲染一行「名称: 值」
 */
const renderKeyValue = (label, value, key) => (
  <p key={key} className="mb-10">
    <span><b>{label}: </b></span>
    <span>{String(value)}</span>
  </p>
);

/**
 * 获取字段展示名；配置是对象时取 _Label，避免把配置对象当成文案渲染
 */
const getFieldLabel = (config, keyName) => {
  const fieldConfig = config?.[keyName];
  if (getType(fieldConfig) === 'object' && fieldConfig !== null) return fieldConfig._Label || keyName;
  return fieldConfig || keyName;
};

/**
 * 判断是否为表格结构，形如 TableItems: [{ Items: [{ Name, Value, Row }] }]
 * 每个元素是「一列/一组单元格」，单元格里的 Row 表示所在行（下标从 0 开始，-1 为非行字段）
 */
const isTableRowsStructure = (value) =>
  Array.isArray(value) &&
  value.length > 0 &&
  value.every((row) => Array.isArray(row?.Items));

/**
 * 渲染表格：把所有单元格按 Row 分组，同一行内的字段横向排列
 * Row 为 -1（非行字段）或缺失的单元格统一放在最后一行
 */
const renderTable = (rows, key) => {
  const cellGroups = new Map();
  rows.forEach((row) => {
    row.Items.forEach((cell) => {
      if (!cell || (!cell.Name && (cell.Value === '' || cell.Value == null))) return;
      const rowIndex = Number.isInteger(cell.Row) ? cell.Row : -1;
      if (!cellGroups.has(rowIndex)) cellGroups.set(rowIndex, []);
      cellGroups.get(rowIndex).push(cell);
    });
  });

  const orderedRowIndexes = [...cellGroups.keys()].sort((a, b) => {
    if (a === -1) return 1;
    if (b === -1) return -1;
    return a - b;
  });

  return (
    <div key={key} className="invoice-table">
      {orderedRowIndexes.map((rowIndex) => (
        <div key={`${key}-row-${rowIndex}`} className="invoice-table__row">
          {cellGroups.get(rowIndex).map((cell, cellIndex) => (
            <span key={`${key}-cell-${rowIndex}-${cellIndex}`} className="invoice-table__cell">
              <b>{cell.Name}: </b>
              {SIMPLE_TYPES.includes(getType(cell.Value)) ? cell.Value : JSON.stringify(cell.Value)}
            </span>
          ))}
        </div>
      ))}
    </div>
  );
};

/**
 * 递归渲染任意形态的字段值
 * 兼容 { OtherInvoiceItemList: [] }、{ Name, Value }、以及任意对象/数组嵌套
 */
const renderValue = (label, value, key, config) => {
  const type = getType(value);

  if (SIMPLE_TYPES.includes(type)) {
    if (value === '') return null;
    return renderKeyValue(label, value, key);
  }

  if (type === 'array') {
    if (value.length === 0) return null;
    if (isTableRowsStructure(value)) return renderTable(value, key);
    return (
      <Fragment key={key}>
        {value.map((item, index) => renderValue(label, item, `${key}-${index}`, config))}
      </Fragment>
    );
  }

  if (type === 'object') {
    if (isObjectEmptyValues(value)) return null;

    if (Array.isArray(value.OtherInvoiceItemList)) {
      return renderValue(label, value.OtherInvoiceItemList, key, config);
    }

    const hasNameValue = value.Name && value.Value !== '' && value.Value !== undefined && value.Value !== null;
    if (hasNameValue) {
      return renderValue(value.Name, value.Value, key, config);
    }

    return (
      <Fragment key={key}>
        {Object.keys(value).map((ckey) => {
          const childConfig = getType(config?.[ckey]) === 'object' && config[ckey] !== null ? config[ckey] : config;
          return renderValue(getFieldLabel(config, ckey), value[ckey], `${key}-${ckey}`, childConfig);
        })}
      </Fragment>
    );
  }

  return null;
};

/**
 * 渲染 SingleInvoiceInfos 下的单个字段
 * 简单类型复用 ChildMapRenderer，对象/数组由 renderValue 递归展开
 */
const renderField = (keyName, value, key, config) => {
  const type = getType(value);

  if (type === 'object' || type === 'array') {
    if (isObjectEmptyValues(value)) return null;
    const childConfig = getType(config?.[keyName]) === 'object' && config[keyName] !== null ? config[keyName] : config;
    return (
      <Form.Item key={key} active label={getFieldLabel(config, keyName)} className="unv-form__item-group">
        {renderValue(keyName, value, key, childConfig)}
      </Form.Item>
    );
  }

  if (SIMPLE_TYPES.includes(type)) {
    if (value === '') return null;
    return (
      <Fragment key={key}>
        <ChildMapRenderer keyName={keyName} value={value} dep={0} config={config} />
      </Fragment>
    );
  }

  return null;
};

/**
 * Invoice 渲染器 - 票据识别结果
 * @param {Object} props
 * @param {Object} props.params - OCR 响应参数
 * @param {Object} props.config - 结果配置
 * @param {Object} props.detail - 菜单详情配置
 * @param {string} props.selectRect - 当前选中的矩形 key
 * @param {Function} props.onSelect - 选中回调
 */
const InvoiceRenderer = ({ params, config, detail, selectRect, onSelect }) => {
  return (
    <>
      {params[config.firstField]?.map((v, vindex) => {
        const OtherTexts = v.SingleInvoiceInfos?.[v.SubType];
        const invoiceDetailConfig = { ...invoiceConfig.default, ...invoiceConfig[v.SubType] };
        const selectRectVal = getSelectRectKey({
          data: v,
          key: detail?.rectConfig?.RectKey,
          valStr: v.SubType
        });

        return (
          <div
            key={`invoice-${vindex}`}
            onClick={() => {
              onSelect && onSelect(v.SubType, selectRectVal);
            }}
            className={`${selectRect && selectRect === selectRectVal ? 'isSelect' : ''} invoiceContent`}
          >
            <Form.Item active label="票据类型" className="unv-form__item-group">
              {config.invoiceTypeMap[v.Type]}
            </Form.Item>
            <Form.Item active label="旋转角度" className="unv-form__item-group">
              {v.Angle}
            </Form.Item>
            <Form.Item active label="位置信息" className="unv-form__item-group">
              {JSON.stringify(v.Polygon)}
            </Form.Item>

            {OtherTexts &&
              Object.keys(OtherTexts).map((k, kindex) => renderField(k, OtherTexts[k], `field-${kindex}`, invoiceDetailConfig))}
          </div>
        );
      })}
    </>
  );
};

export default InvoiceRenderer;
